import React, { useState } from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import LegalDocumentModal from "./LegalDocumentModal";

/**
 * LegalAcceptanceCard
 *
 * Renders the mandatory legal acceptance block above payment buttons.
 * Props:
 *   accepted     {boolean}  — current checkbox state
 *   onChange     {fn}       — called with true/false when checkbox changes
 *   error        {string}   — inline validation error message (empty = no error)
 */
const LegalAcceptanceCard = ({ accepted, onChange, error }) => {
  const [openModal, setOpenModal] = useState(null); // "terms" | "privacy" | null

  const linkStyle = {
    color: "#6D4AFF",
    fontWeight: 700,
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    display: "inline",
  };

  return (
    <>
      <div
        style={{
          background: "#FFFFFF",
          border: accepted
            ? "1.5px solid #A5B4FC"
            : error
            ? "1.5px solid #FCA5A5"
            : "1.5px solid #E2E8F0",
          borderRadius: "16px",
          padding: "20px 22px",
          marginBottom: "14px",
          boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
          transition: "border-color 200ms",
          fontFamily: "'Inter','Segoe UI',sans-serif",
        }}
      >
        {/* Card header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px",
          }}
        >
          <div
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              background: accepted
                ? "rgba(109,74,255,0.12)"
                : "rgba(109,74,255,0.07)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 200ms",
            }}
          >
            <ShieldCheck
              style={{
                width: "17px",
                height: "17px",
                color: "#6D4AFF",
              }}
            />
          </div>
          <div>
            <p
              style={{
                fontSize: "13px",
                fontWeight: 800,
                color: "#0F172A",
                margin: 0,
              }}
            >
              Terms &amp; Privacy Acceptance
            </p>
            <p
              style={{
                fontSize: "11px",
                color: "#94A3B8",
                margin: 0,
                fontWeight: 500,
              }}
            >
              Please review and accept our Terms &amp; Conditions and Privacy
              Policy before proceeding with payment.
            </p>
          </div>
        </div>

        {/* Checkbox row */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => onChange(e.target.checked)}
            aria-label="I have read, understood and agree to the Terms & Conditions and Privacy Policy"
            style={{
              width: "18px",
              height: "18px",
              accentColor: "#6D4AFF",
              cursor: "pointer",
              marginTop: "1px",
              flexShrink: 0,
              borderRadius: "4px",
            }}
          />
          <span
            style={{
              fontSize: "13px",
              color: "#374151",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            I have read, understood and agree to the{" "}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal("terms");
              }}
              style={linkStyle}
              aria-label="Open Terms and Conditions document"
            >
              Terms &amp; Conditions
            </button>{" "}
            and{" "}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal("privacy");
              }}
              style={linkStyle}
              aria-label="Open Privacy Policy document"
            >
              Privacy Policy
            </button>
            .
          </span>
        </label>

        {/* Inline validation error */}
        {error && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "10px",
            }}
          >
            <AlertCircle
              style={{
                width: "13px",
                height: "13px",
                color: "#EF4444",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: "12px",
                color: "#DC2626",
                fontWeight: 500,
              }}
            >
              {error}
            </span>
          </div>
        )}
      </div>

      {/* Modals */}
      <LegalDocumentModal
        isOpen={openModal === "terms"}
        documentType="terms"
        onClose={() => setOpenModal(null)}
      />
      <LegalDocumentModal
        isOpen={openModal === "privacy"}
        documentType="privacy"
        onClose={() => setOpenModal(null)}
      />
    </>
  );
};

export default LegalAcceptanceCard;
